import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AutomationStep } from "@/types/Automation";
import type { StepNodeData } from "../types";

interface MetaGraphItem {
  id: string;
  name?: string;
  is_value_based?: boolean;
}

export function MetaAddToAudienceFields({
  step,
  onMetaAddToAudienceChange,
}: {
  step: AutomationStep;
  onMetaAddToAudienceChange: StepNodeData["onMetaAddToAudienceChange"];
}) {
  const businessId = (step.data?.businessId as string) || "";
  const adAccountId = (step.data?.adAccountId as string) || "";
  const audienceId = (step.data?.audienceId as string) || "";

  const [businesses, setBusinesses] = useState<MetaGraphItem[]>([]);
  const [adAccounts, setAdAccounts] = useState<MetaGraphItem[]>([]);
  const [audiences, setAudiences] = useState<MetaGraphItem[]>([]);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(true);
  const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(false);
  const [isLoadingAudiences, setIsLoadingAudiences] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchBusinesses() {
      setIsLoadingBusinesses(true);
      try {
        const response = await fetch("/api/meta-account?type=facebook");
        if (!response.ok) return;
        const data = (await response.json()) as { businesses?: MetaGraphItem[] };
        if (!cancelled) {
          setBusinesses(data.businesses ?? []);
        }
      } catch (error) {
        console.error("Failed to fetch Meta businesses:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingBusinesses(false);
        }
      }
    }

    fetchBusinesses();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!businessId) {
      setAdAccounts([]);
      return;
    }

    let cancelled = false;

    async function fetchAdAccounts() {
      setIsLoadingAdAccounts(true);
      try {
        const response = await fetch(
          `/api/meta/businesses/${businessId}/owned_ad_accounts?fields=name`
        );
        if (!response.ok) return;
        const data = (await response.json()) as { adAccounts?: MetaGraphItem[] };
        if (!cancelled) {
          setAdAccounts(data.adAccounts ?? []);
        }
      } catch (error) {
        console.error("Failed to fetch Meta ad accounts:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingAdAccounts(false);
        }
      }
    }

    fetchAdAccounts();

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!adAccountId) {
      setAudiences([]);
      return;
    }

    let cancelled = false;

    async function fetchAudiences() {
      setIsLoadingAudiences(true);
      try {
        const response = await fetch(
          `/api/meta/ad-accounts/${adAccountId}/customaudiences?fields=name,is_value_based`
        );
        if (!response.ok) return;
        const data = (await response.json()) as { audiences?: MetaGraphItem[] };
        if (!cancelled) {
          setAudiences(data.audiences ?? []);
        }
      } catch (error) {
        console.error("Failed to fetch Meta audiences:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingAudiences(false);
        }
      }
    }

    fetchAudiences();

    return () => {
      cancelled = true;
    };
  }, [adAccountId]);

  return (
    <div className="p-4 space-y-3 nodrag" onPointerDownCapture={(e) => e.stopPropagation()}>
      <p className="text-xs text-muted-foreground">
        Adds the lead to a Meta custom audience using their email or phone. Value-based
        audiences also receive the lead&apos;s amount spent as the customer value.
      </p>
      <div className="space-y-2">
        <Label className="text-xs text-blue-800">Business account</Label>
        {isLoadingBusinesses ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : businesses.length === 0 ? (
          <p className="text-xs text-muted-foreground">No business accounts found.</p>
        ) : (
          <Select
            value={businessId || undefined}
            onValueChange={(val) =>
              onMetaAddToAudienceChange({
                stepId: step.id,
                businessId: val,
                adAccountId: "",
                audienceId: "",
              })
            }
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select business" />
            </SelectTrigger>
            <SelectContent>
              {businesses.map((business) => (
                <SelectItem key={business.id} value={business.id}>
                  {business.name || business.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {businessId ? (
        <div className="space-y-2">
          <Label className="text-xs text-blue-800">Ad account</Label>
          {isLoadingAdAccounts ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </div>
          ) : adAccounts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No ad accounts found.</p>
          ) : (
            <Select
              value={adAccountId || undefined}
              onValueChange={(val) =>
                onMetaAddToAudienceChange({
                  stepId: step.id,
                  adAccountId: val,
                  audienceId: "",
                })
              }
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select ad account" />
              </SelectTrigger>
              <SelectContent>
                {adAccounts.map((adAccount) => (
                  <SelectItem key={adAccount.id} value={adAccount.id}>
                    {adAccount.name || adAccount.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}
      {adAccountId ? (
        <div className="space-y-2">
          <Label className="text-xs text-blue-800">Audience</Label>
          {isLoadingAudiences ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </div>
          ) : audiences.length === 0 ? (
            <p className="text-xs text-muted-foreground">No audiences found.</p>
          ) : (
            <Select
              value={audienceId || undefined}
              onValueChange={(val) =>
                onMetaAddToAudienceChange({
                  stepId: step.id,
                  audienceId: val,
                })
              }
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select audience" />
              </SelectTrigger>
              <SelectContent>
                {audiences.map((audience) => (
                  <SelectItem key={audience.id} value={audience.id}>
                    {audience.name || audience.id}
                    {audience.is_value_based ? " (Value-based)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}
    </div>
  );
}
